import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Fade,
    Grid,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material';
import { useGetFaqsQuery, useDeleteFaqGroupMutation } from '..';
import { CardListRow, FormDrawer } from '@starjack/shared-components';
import { Fragment, useState } from 'react';
import { BoldLabel } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import { createEmptyFaqGroup, FaqGroup } from '../model/faq.types';
import { GroupDrawerContent } from './groupDrawerContent';
import { useNotification } from '@starjack/features/notifications';
import {
    ConfirmationDialog,
    LoadingContainer,
} from '@starjack/shared-components';

export const FaqGroupConfiguration = () => {
    const { data } = useGetFaqsQuery({
        filtered: true,
    });
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        group: FaqGroup;
    }>({ isOpen: false, group: null });
    const [deleteFaqGroup] = useDeleteFaqGroupMutation();
    const { addNotification } = useNotification();
    const [deleteGroupId, setDeleteGroupId] = useState<string>('');
    const [groupToDelete, setGroupToDelete] = useState<FaqGroup | null>(null);

    const onDelete = (faqGroup: FaqGroup) => {
        setDeleteGroupId(faqGroup.id);
        deleteFaqGroup({ faqGroupId: faqGroup.id })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Themenbereich erfolgreich gelöscht',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Löschen. Bitte später noch einmal versuchen',
                });
            })
            .finally(() => {
                setDeleteGroupId(null);
            });
    };

    const onCloseDrawer = () => {
        setDrawerConfig({
            isOpen: false,
            group: null,
        });
    };

    return (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth='lg'
        >
            <Card>
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h6'>
                        Angelegte Themenbereiche
                    </Typography>

                    <Button
                        onClick={() => {
                            setDrawerConfig({
                                isOpen: true,
                                group: null,
                            });
                        }}
                        sx={{
                            ml: 'auto',
                        }}
                        variant='contained'
                    >
                        Themenbereich erstellen
                    </Button>
                </CardContent>
                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                {data?.map((group, index) => (
                    <Fragment key={index}>
                        <CardListRow
                            sx={{
                                position: 'relative',
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    flexGrow: 1,
                                }}
                            >
                                <Grid spacing={6} container>
                                    <Grid md={6} item>
                                        <BoldLabel>Deutsch:</BoldLabel>{' '}
                                        {group.name.de}
                                    </Grid>
                                    <Grid md={6} item>
                                        <BoldLabel>Priorität:</BoldLabel>{' '}
                                        {group.priority}
                                    </Grid>
                                    <Grid md={6} item>
                                        <BoldLabel>Englisch:</BoldLabel>{' '}
                                        {group.name.en || '- '}
                                    </Grid>
                                </Grid>
                            </Box>

                            <Stack
                                sx={{
                                    ml: 'auto',
                                    alignItems: 'center',
                                }}
                                direction='row'
                                spacing={2}
                            >
                                <IconButton
                                    aria-label='Kategorie bearbeiten'
                                    onClick={() => {
                                        setDrawerConfig({
                                            isOpen: true,
                                            group,
                                        });
                                    }}
                                >
                                    <SvgIcon>
                                        <EditIcon />
                                    </SvgIcon>
                                </IconButton>

                                <IconButton
                                    aria-label='Kategorie löschen'
                                    onClick={() => setGroupToDelete(group)}
                                >
                                    <SvgIcon>
                                        <DeleteIcon />
                                    </SvgIcon>
                                </IconButton>
                            </Stack>
                            <Fade in={deleteGroupId === group.id}>
                                <div>
                                    <LoadingContainer cover />
                                </div>
                            </Fade>
                        </CardListRow>

                        {index < data.length - 1 && (
                            <Divider
                                sx={{
                                    width: '100%',
                                }}
                            />
                        )}
                    </Fragment>
                ))}
            </Card>

            <ConfirmationDialog
                open={!!groupToDelete}
                onClose={() => setGroupToDelete(null)}
                onConfirm={() => {
                    onDelete(groupToDelete);
                    setGroupToDelete(null);
                }}
                body={`Kategorie „${groupToDelete?.name?.de}“ wirklich löschen?`}
                confirmButtonText='Löschen'
            />

            <FormDrawer
                anchor='right'
                open={drawerConfig.isOpen}
                onClose={onCloseDrawer}
            >
                {/* key remounts per group; the empty group is rebuilt on every render */}
                <GroupDrawerContent
                    key={drawerConfig?.group?.id ?? 'new'}
                    group={drawerConfig?.group || createEmptyFaqGroup()}
                    onClose={onCloseDrawer}
                />
            </FormDrawer>
        </Container>
    );
};
